import * as errors from '@attain-apps/errors'
import * as store from '@attain-apps/fe/web/main/lib/store'
import { app } from '@microsoft/teams-js'
import * as awsAmplifyAuth from 'aws-amplify/auth'
import * as rxjs from 'rxjs'

import type { Feature } from '../feature'

const MAX_RETRIES = 3
const RETRY_TIME = 50

/**
 * If we haven't gotten a response (success or error) from attempting to
 * authenticate after this number of milliseconds, we count it as a failure and
 * potentially try again
 */
const MAX_AUTH_TIME = 2e3

const TEAMS_INIT_TIMEOUT = 2000 // 2s timeout, adjustable

// If Attain Apps is being used within Teams its app will initialize, otherwise regular login flow
async function isInTeams(): Promise<boolean> {
  console.debug('Detected Ms Teams: logging in with EntraId')
  if ((window as any).Cypress) {
    // To avoid application false detecting teams environment in cypress sandbox
    // We manually check if its ran inside Cypress
    return false
  }

  try {
    await Promise.race([
      app.initialize(),
      new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error('Teams init timeout')),
          TEAMS_INIT_TIMEOUT,
        ),
      ),
    ])
    return true
  } catch {
    return false
  }
}

export const executeAuth = store.effects.create((feature: Feature) =>
  feature.actions.authenticate.request.observe().pipe(
    rxjs.switchMap(action =>
      _tryGetAuthSession().pipe(
        rxjs.map(session =>
          session
            ? feature.actions.authenticate.success
                .from(action)
                .create({ response: { session } })
            : feature.actions.authenticate.error
                .from(action)
                // TODO: find out the reason, if possible?
                .create({
                  error: new errors.NotFoundError({ type: 'user session' }),
                }),
        ),
        rxjs.catchError(error =>
          rxjs.of(
            feature.actions.authenticate.error.from(action).create({ error }),
          ),
        ),
      ),
    ),
  ),
)

function _tryGetAuthSession(
  _maxRetries = MAX_RETRIES,
): rxjs.Observable<awsAmplifyAuth.AuthSession | undefined> {
  return rxjs
    .defer(async () => awsAmplifyAuth.fetchAuthSession())
    .pipe(
      rxjs.switchMap(session => {
        if (session.tokens) {
          return rxjs.of(session)
        }
        if (_maxRetries > 0) {
          return rxjs.of(undefined).pipe(
            rxjs.delay(RETRY_TIME),
            rxjs.switchMap(() => _tryGetAuthSession(_maxRetries - 1)),
          )
        }
        throw new Error('Failed to get current user')
      }),
      rxjs.catchError(error => {
        console.error('Login error', error)
        // If we failed to get the session, we try and create a new session by
        // signing in through hosted UI. In this case, we don't emit anything
        // because this should lead to a redirect to a different site
        return rxjs.from(isInTeams()).pipe(
          rxjs.switchMap(inTeams =>
            rxjs
              .from(
                awsAmplifyAuth.signInWithRedirect(
                  inTeams
                    ? {
                        provider: { custom: 'Microsoft' },
                      }
                    : undefined,
                ),
              )
              .pipe(rxjs.ignoreElements()),
          ),
        )
      }),
      rxjs.timeout({
        first: MAX_AUTH_TIME,
        with: () => {
          console.debug('Login timed out')
          return _maxRetries > 0
            ? _tryGetAuthSession(_maxRetries - 1)
            : rxjs.of(undefined)
        },
      }),
    )
}
